// SPDX-License-Identifier: Apache-2.0
import Feature from 'ol/Feature';
import AbstractSearchClient, { BaseSearchClientConfig } from './abstractsearchclient';
import IGirafeContext from '../context/icontext';
import { GeoJSON } from 'ol/format';
import { Extent } from 'ol/extent';
import { transformExtent } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';

export type GeoAdminChSearchClientConfig = BaseSearchClientConfig & {
  type: 'geoadminch';
  bbox?: string;
  locationOrigins?: string;
};

const locationOrigins = ['zipcode', 'gg25', 'district', 'kantone', 'gazetteer', 'address', 'parcel'];

/**
 * Search provider for the Swiss search service of geo.admin.ch.
 * See documentation at https://docs.geo.admin.ch/access-data/search.html
 *
 * The GeoAdmin search provider allows 3 different types of searches:
 * - locations: POIs like addresses, swissnames, boundaries, parcels
 * - layers: layers of the GeoAdmin portal
 * - features: features inside one or multiple layers of the GeoAdmin portal
 *
 * This client currently only supports location search.
 * The API returns max 50 items per request.
 *
 * Configuration:
 * - providerName: The name of the search provider, or geoadminch.
 * - resultSrid: The spatial reference system (SRID) of the search results, always EPSG:2056.
 * - bbox: Comma-separated list of bounding box coordinates in EPSG:2056 (e.g. "2580000,1150000,2600000,1200000")
 *         If no bbox is set, the max extent of the map is used.
 * - locationOrigins: Array of categories that are searched. Default: All categories.
 *            Available categories and data origin:
 *            - zipcode (ch.swisstopo-vd.ortschaftenverzeichnis_plz)
 *            - gg25 (ch.swisstopo.swissboundaries3d-gemeinde-flaeche.fill)
 *            - district (ch.swisstopo.swissboundaries3d-bezirk-flaeche.fill)
 *            - kantone (ch.swisstopo.swissboundaries3d-kanton-flaeche.fill)
 *            - gazetteer (ch.swisstopo.swissnames3d, ch.bav.haltestellen-oev)
 *            - address (ch.swisstopo.amtliches-gebaeudeadressverzeichnis, including EGID)
 *            - parcel
 */
class GeoAdminChSearchClient extends AbstractSearchClient {
  protected override searchTermQueryParameter: string = 'searchText';
  protected override searchLangQueryParameter: string = 'lang';
  protected override searchFilterQueryParameter = 'origins';
  private readonly locationOrigins: string[] = locationOrigins;
  private readonly hasLimitedOriginsConfigured: boolean = false;
  private readonly i18nKeyPrefix: string = 'geoadminch_';

  public constructor(context: IGirafeContext, config: GeoAdminChSearchClientConfig) {
    config.url = 'https://api3.geo.admin.ch/rest/services/api/SearchServer';
    config.resultSrid = 'EPSG:2056';
    super(context, config);

    this.url.searchParams.set('type', 'locations');
    this.url.searchParams.set('sr', this.resultSrid.replace('EPSG:', ''));
    this.url.searchParams.set('geometryFormat', 'geojson');
    this.url.searchParams.set('sortbox', 'false');
    if (config.locationOrigins) {
      this.locationOrigins = config.locationOrigins.split(',');
      this.hasLimitedOriginsConfigured = true;
    }
    this.setSearchExtent(config.bbox);
  }

  private setSearchExtent(bbox?: string) {
    let searchExtent: string | undefined;
    if (bbox) {
      searchExtent = bbox;
    } else {
      // If no bbox is configured, set the search extent to the max map extent
      searchExtent = this.context.configManager.Config.map.maxExtent;
    }
    if (!searchExtent) {
      return;
    }
    // If the default map extent isn't specified in EPSG:2056, transform it
    const defaultSrid = this.context.configManager.getDefaultConfigValue('map.srid') as string;
    if (defaultSrid !== this.resultSrid) {
      const extent: Extent = searchExtent.split(',').map(Number);
      searchExtent = transformExtent(extent, defaultSrid, this.resultSrid).join(',');
    }
    this.url.searchParams.set('bbox', searchExtent);
  }

  public override async getSearchCategories(): Promise<string[]> {
    return this.locationOrigins;
  }

  protected override buildRequestUrl(term: string, filter: string[]): URL {
    if (this.hasLimitedOriginsConfigured && !filter.length) {
      // Apply the limitations to searchable origins from config.json
      filter = this.locationOrigins;
    }
    return super.buildRequestUrl(term, filter);
  }

  protected override async parseResponse(response: Response): Promise<Feature[]> {
    const data = this.fixGeoAdminCoordinates((await response.json()) as GeoJSON);

    const features = this.geoJsonFormatter.readFeatures(data, {
      dataProjection: this.resultSrid,
      featureProjection: this.mapProjection
    });

    // Property `label` contains an HTML string. Rename `label` to `label_html` to use in the dropdown,
    // and sanitize `label` to be used in the input field
    return features.map((f) => {
      f.set('provider', this.providerName);
      f.set('label_html', f.get('label') as string);
      f.set('label', this.sanitizeHtmlLabel(f.get('label')));
      return f;
    });
  }

  /**
   * GeoAdmin geoJson responses contain coordinates as [north, east], but the GeoJson standard is [east, north].
   * To be able to use the ol GeoJson format reader, the coordinates are rearranged.
   * Note that other spatial information in the GeoAdmin response is valid, e.g. the bbox has the correct order.
   */
  private fixGeoAdminCoordinates(data: GeoJSON): GeoJSON {
    if ('features' in data && Array.isArray(data.features)) {
      for (const feature of data.features) {
        if (feature.geometry) {
          feature.geometry.coordinates = this.swapCoordinates(feature.geometry.coordinates);
        }
      }
    }
    return data;
  }

  /**
   * Swap first (north) and second (east) coordinate in an array of coordinates.
   */
  private swapCoordinates(coordinates: unknown): unknown {
    if (
      Array.isArray(coordinates) &&
      coordinates.length >= 2 &&
      typeof coordinates[0] === 'number' &&
      typeof coordinates[1] === 'number' &&
      coordinates[0] < coordinates[1]
    ) {
      const pos = coordinates as Coordinate;
      return [pos[1], pos[0], ...pos.slice(2)];
    }
    return (coordinates as unknown[]).map(this.swapCoordinates);
  }

  /**
   * The property `origin` contains the result category.
   */
  public override groupedResults(results: Feature[]): Record<string, Feature[]> {
    const groupedResults: Record<string, Feature[]> = {};
    for (const result of results) {
      const group = this.getI18nKeyForCategory(result.get('origin') ?? 'other');
      groupedResults[group] ??= [];
      groupedResults[group].push(result);
    }
    return groupedResults;
  }

  public override getZoomToBbox(feature: Feature): Extent | undefined {
    const st_bbox = feature.get('geom_st_box2d') as string;
    if (st_bbox) {
      const bbox_coords = st_bbox.replace('BOX(', '').replace(')', '').replaceAll(' ', ',');
      const extent = bbox_coords.split(',').map((coord) => Number.parseFloat(coord)) as Extent;
      return transformExtent(extent, this.resultSrid, this.mapProjection);
    }
    return undefined;
  }

  /**
   * Remove bold and italic HTML tags from the label
   */
  private sanitizeHtmlLabel(label: string): string {
    return label.replace(/<\/?[ib]>/g, '');
  }

  public override getI18nKeyForCategory(category: string): string {
    return `${this.i18nKeyPrefix}${category}`;
  }
}

export default GeoAdminChSearchClient;
